import React, { FormEvent, ReactNode, useState } from 'react';
import { IListItem } from '@nkr/models/index';
import { Button, ControlLabel, FormControl, FormGroup, Glyphicon } from 'react-bootstrap';

interface IProps<T> {
    itemList: IListItem[],
    assignedList: T[],
    getItemId: (item: T) => number,
    createAssignedItem: (id: number) => T,
    renderItemHeader?: ReactNode,
    renderItemOptions?: (item: T, onItemChange: (updated: T) => void) => ReactNode,
    availableLabel?: string,
    enableAvailableSearch?: boolean,
    availableSearchPlaceholder?: string,
    assignedLabel?: string,
    enableAssignedSearch?: boolean,
    assignedSearchPlaceholder?: string,
    onChange: (list: T[]) => void
}

const MultiPickList = <T,>({
    itemList, assignedList, getItemId, createAssignedItem,
    renderItemHeader, renderItemOptions,
    availableLabel, enableAvailableSearch, availableSearchPlaceholder,
    assignedLabel, enableAssignedSearch, assignedSearchPlaceholder,
    onChange
}: IProps<T>) => {
    const [selectedAvailable, setSelectedAvailable] = useState<string[]>([]);
    const [availableSearch, setAvailableSearch] = useState('');
    const [selectedAssigned, setSelectedAssigned] = useState<number[]>([]);
    const [assignedSearch, setAssignedSearch] = useState('');

    const handleAvailableClick = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let options = Array.from(e.currentTarget.selectedOptions);
        let values = options.map((option) => option.value);
        setSelectedAvailable(values);
    };

    const handleSelectedClick = (id: number) => {
        setSelectedAssigned(selectedAssigned.indexOf(id) >= 0
            ? selectedAssigned.filter(x => x != id)
            : [...selectedAssigned, id]);
    };

    const handleItemChange = (id: number, updated: T) => {
        onChange(assignedList.map(x => getItemId(x) == id ? updated : x));
    };

    const handleAdd = () => {
        let newList = [...assignedList, ...selectedAvailable.map(x => createAssignedItem(parseInt(x)))];

        onChange(newList);
        setSelectedAvailable([]);
    };

    const handleRemove = () => {
        let newList = assignedList.filter(x => selectedAssigned.indexOf(getItemId(x)) < 0);

        onChange(newList);
        setSelectedAssigned([]);
    };

    const handleSearchChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;

        if (name == 'availableSearch') {
            setAvailableSearch(value);
        } else {
            setAssignedSearch(value);
        }
    };

    const assignedIds = assignedList.map(getItemId);

    const filteredAvailableList = itemList.filter(x => {
        let assignedFilter = assignedIds.indexOf(x.id) < 0
        let textFilter = availableSearch.length > 2 ? x.description.toUpperCase().indexOf(availableSearch.toUpperCase()) >= 0 : true;
        return assignedFilter && textFilter;
    });

    const filteredAssignedList = itemList
        .filter(x => {
            let assignedFilter = assignedIds.indexOf(x.id) >= 0;
            let textFilter = assignedSearch.length > 2 ? x.description.toUpperCase().indexOf(assignedSearch.toUpperCase()) >= 0 : true;
            return assignedFilter && textFilter;
        })
        .map(x => ({ listItem: x, assignedItem: assignedList.find(a => getItemId(a) == x.id) }));

    return (
        <div>
            <div style={{ display: 'flex', minHeight: 350 }}>
                <div style={{ width: '42.5%' }}>
                    <FormGroup style={{ height: '90%' }}>
                        <ControlLabel>{availableLabel ?? 'Available Items'}</ControlLabel>

                        {enableAvailableSearch &&
                            <FormControl
                                name='availableSearch'
                                bsSize='sm'
                                placeholder={availableSearchPlaceholder}
                                value={availableSearch}
                                onChange={handleSearchChange}
                            />
                        }

                        <FormControl
                            componentClass='select'
                            multiple
                            bsSize='sm'
                            style={{ height: '95%' }}
                            value={selectedAvailable}
                            onChange={handleAvailableClick}
                        >
                            {filteredAvailableList.map((item, index) =>
                                <option key={'available-' + index} value={item.id}>{item.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                </div>

                <div style={{ width: '15%', padding: '35px 5px 0px 5px', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                    <Button bsSize='xs' block onClick={handleAdd}>Add <Glyphicon glyph='triangle-right' /></Button>
                    <Button bsSize='xs' block onClick={handleRemove}><Glyphicon glyph='triangle-left' /> Remove</Button>
                </div>

                <div style={{ width: '42.5%' }}>
                    <FormGroup style={{ height: '90%' }}>
                        <ControlLabel>{assignedLabel ?? 'Selected Items'}</ControlLabel>

                        {enableAssignedSearch &&
                            <FormControl
                                name='assignedSearch'
                                bsSize='sm'
                                placeholder={assignedSearchPlaceholder}
                                value={assignedSearch}
                                onChange={handleSearchChange}
                            />
                        }

                        <div style={{ height: '95%', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 3 }}>
                            {renderItemHeader}

                            {filteredAssignedList.map(({ listItem, assignedItem }) => {
                                let isSelected = selectedAssigned.indexOf(listItem.id) >= 0;

                                return (
                                    <div
                                        key={'assigned-' + listItem.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '2px 6px',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#337ab7' : 'transparent',
                                            color: isSelected ? '#fff' : 'inherit'
                                        }}
                                        onClick={() => handleSelectedClick(listItem.id)}
                                    >
                                        <div style={{ width: '55%' }}>{listItem.description}</div>

                                        {renderItemOptions &&
                                            <div style={{ width: '45%', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                {renderItemOptions(assignedItem, updated => handleItemChange(listItem.id, updated))}
                                            </div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </FormGroup>
                </div>
            </div>
        </div>
    );
}

export default MultiPickList;